/**
 * Hard Reload Banner Component
 *
 * Shows a top navigation banner when shouldShow function returns true.
 * For now, has a dummy function that shows the banner every 2 minutes.
 * Displays a clean inline banner similar to popular websites.
 */

import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

// Local storage key for last reload time
const LAST_RELOAD_TIME_KEY = 'last_hard_reload_time';

const HardReloadButton: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Shows banner every 6 hours
  const shouldShowBanner = (): boolean => {
    const lastReloadTime = localStorage.getItem(LAST_RELOAD_TIME_KEY);

    if (!lastReloadTime) {
      return true;
    }

    const lastReload = new Date(lastReloadTime).getTime();
    const now = Date.now();
    const hoursSinceLastReload = (now - lastReload) / (1000 * 60 * 60);

    return hoursSinceLastReload >= 6;
  };

  useEffect(() => {
    const checkBannerVisibility = () => {
      setShowBanner(shouldShowBanner());
    };

    checkBannerVisibility();

    const interval = setInterval(() => {
      checkBannerVisibility();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleHardReload = () => {
    setIsReloading(true);

    localStorage.setItem(LAST_RELOAD_TIME_KEY, new Date().toISOString());

    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  if (!showBanner || isDismissed) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] hard-reload-banner">
      <div className="bg-blue-500 text-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 flex-1">
            <RefreshCw className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              A new version is available.{' '}
              <button
                onClick={handleHardReload}
                disabled={isReloading}
                className="underline hover:no-underline font-semibold ml-1 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isReloading ? 'Reloading...' : 'Click to reload'}
              </button>
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default HardReloadButton;
